'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Cross2Icon } from '@radix-ui/react-icons';
import { ChevronDown, ChevronUp, Info } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import { IntegrationToken } from '@/features/integrations/api/get-integration-tokens';
import { IntegrationTokenCard } from '@/features/integrations/components/cards/integration-token-card';
import { IntegrationSetupHoverOverlay } from '@/features/integrations/components/integration-setup-hover-overlay';
import { ZapierLogo } from '@/features/integrations/components/zapier-logo';
import { useProjectKpis } from '@/features/projects/project-details/hooks/use-project-kpis';
import { Gradients } from '@/shared/components/theme/gradient';
import { Button } from '@/shared/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/components/ui/collapsible';
import { Dialog } from '@/shared/components/ui/dialog';
import { cn } from '@/shared/utils/cn';

interface ViewIntegrationTokensDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tokens: IntegrationToken[];
  projectId: number;
}

export const ViewIntegrationTokensDialog = ({
  open,
  onOpenChange,
  tokens,
  projectId,
}: ViewIntegrationTokensDialogProps) => {
  const { t } = useTranslation('integrations', {
    keyPrefix: 'ViewTokensDialog',
  });

  const navigate = useNavigate();

  const { data: projectKpis } = useProjectKpis({
    projectId: projectId,
    projectType: 'subproject_contractor',
  });

  const [isKpisOpen, setIsKpisOpen] = useState(false);

  const handleManageTokens = () => {
    onOpenChange(false);
    navigate(`${paths.settings.getHref()}?tab=integrations`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/20 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-[50%] top-[50%] z-50 w-[95vw] max-w-2xl translate-x-[-50%] translate-y-[-50%] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
            Gradients.overlayGradient,
            'rounded-[10px] border border-white/15',
            'backdrop-blur-xl',
          )}
        >
          <div
            className={cn(
              'absolute inset-0',
              Gradients.transparentWhiteGradient,
            )}
          />
          <div className="relative max-h-[85vh] overflow-y-auto">
            <div className="p-8">
              {/* Header */}
              <div className="relative mb-8 mt-4">
                <div className="flex items-center justify-between">
                  <DialogPrimitive.Title className="text-2xl font-semibold text-white">
                    {t('title')}
                  </DialogPrimitive.Title>
                  <div className="flex items-center gap-2">
                    <IntegrationSetupHoverOverlay>
                      <button
                        type="button"
                        className="flex size-6 items-center justify-center rounded-full border border-white/30 bg-white/10 text-white transition-opacity hover:opacity-80"
                        aria-label="Information"
                      >
                        <Info className="size-4" />
                      </button>
                    </IntegrationSetupHoverOverlay>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-8">
                {/* Zapier Logo Section */}
                <div className="flex items-center justify-center">
                  <ZapierLogo />
                </div>

                {/* Project KPIs Section */}
                {projectKpis && projectKpis.length > 0 && (
                  <Collapsible open={isKpisOpen} onOpenChange={setIsKpisOpen}>
                    <CollapsibleTrigger className="w-full">
                      <div className="flex w-full items-center justify-between rounded-lg bg-light-container-alt p-4 transition-opacity hover:opacity-90">
                        <h3 className="text-lg font-semibold text-white">
                          {t('project_kpis_title' as any)}
                        </h3>
                        {isKpisOpen ? (
                          <ChevronUp className="size-5 text-white/60" />
                        ) : (
                          <ChevronDown className="size-5 text-white/60" />
                        )}
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="mt-3 flex flex-col gap-3">
                        {projectKpis.map((kpi) => (
                          <div
                            key={kpi.id}
                            className="flex flex-row items-center justify-between rounded-lg bg-light-container-alt p-4"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-medium text-white/60">
                                ID: {kpi.id}
                              </span>
                              <span className="h-4 w-px bg-white/10" />
                              <span className="font-semibold text-white">
                                {kpi.name}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {/* Active Tokens Section */}
                <div className="flex flex-col gap-4">
                  <h3 className="text-lg font-semibold text-white">
                    {t('active_tokens_title')}
                  </h3>
                  {tokens.length > 0 ? (
                    <div className="flex flex-col gap-3">
                      {tokens.map((token) => (
                        <IntegrationTokenCard key={token.id} token={token} />
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-lg bg-purple-800/20 p-6 text-center">
                      <p className="text-sm text-white/60">{t('no_tokens')}</p>
                    </div>
                  )}
                </div>

                {/* Footer with Manage Button */}
                <div className="flex justify-center">
                  <Button
                    variant="ghost"
                    btnType="primary"
                    onClick={handleManageTokens}
                    className="min-h-11 w-full"
                  >
                    {t('manage_button')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <Cross2Icon className="size-6 stroke-white" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </Dialog>
  );
};
